import type { SalonProfile } from '@/types'

import { formatTime12 } from '@/lib/format'

export const formatOpenDays = (closedDays: Array<string>): string => {
    if (!closedDays.length) return 'Open every day'

    return `Closed on ${closedDays.join(' and ')}`
}

export const formatHours = (profile: Pick<SalonProfile, 'open_time' | 'close_time'>): string => `${formatTime12(profile.open_time)} to ${formatTime12(profile.close_time)}`

export const fullAddress = (profile: SalonProfile): string => [
    profile.address,
    profile.area,
    profile.city,
    [profile.state, profile.pincode].filter(Boolean).join(' ')
].filter(Boolean).join(', ')

export const mapQuery = (profile: SalonProfile): string => profile.map_query || fullAddress(profile) || profile.name

export const mapEmbedUrl = (profile: SalonProfile): string => `https://www.google.com/maps?q=${encodeURIComponent(mapQuery(profile))}&z=16&output=embed`

export const mapDirectionsUrl = (profile: SalonProfile): string => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mapQuery(profile))}`

export const bookingWhatsAppText = (profile: SalonProfile): string => `Hi ${profile.short_name || profile.name}, I would like to book an appointment.`

// The public address used inside emails and WhatsApp messages. Set it in the salon profile once a custom domain is live.
export const siteUrl = (profile: SalonProfile, fallbackOrigin = ''): string => (profile.website_url || fallbackOrigin).replace(/\/+$/, '')

// schema.org BeautySalon data for search engines.
export const salonJsonLd = (profile: SalonProfile, origin: string): Record<string, unknown> => {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].filter(day => !profile.closed_days.includes(day))

    return {
        '@context': 'https://schema.org',
        '@type': 'BeautySalon',
        name: profile.name,
        description: profile.tagline ?? undefined,
        url: origin || undefined,
        image: profile.hero_image ?? undefined,
        telephone: profile.phone ?? undefined,
        email: profile.email ?? undefined,
        priceRange: profile.price_range,
        address: {
            '@type': 'PostalAddress',
            streetAddress: profile.address ?? undefined,
            addressLocality: [profile.area, profile.city].filter(Boolean).join(', ') || undefined,
            addressRegion: profile.state ?? undefined,
            postalCode: profile.pincode ?? undefined,
            addressCountry: 'IN'
        },
        openingHoursSpecification: [{
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: days,
            opens: profile.open_time,
            closes: profile.close_time
        }],
        sameAs: [profile.instagram_url, profile.facebook_url, profile.youtube_url].filter(Boolean)
    }
}
